<template>
  <div class="floating-notifications">
    <!-- Con una sola notificación su ✕ alcanza; arriba del grupo y con texto
         para que se entienda que cierra todas (no borra nada). -->
    <div v-if="notifications.length > 1" class="clear-buttons">
      <button
        type="button"
        @click="clearAllNotifications"
        class="btn btn-sm btn-light shadow-sm"
      >
        <i class="fas fa-times me-1"></i>{{ $t("Close all") }}
      </button>
    </div>

    <transition-group name="notification" tag="div">
      <div
        v-for="notification in notifications"
        :key="notification.id"
        class="notification-item"
        :class="notification.type"
      >
        <div class="notification-content">
          <div class="notification-header">
            <div
              class="d-flex justify-content-between align-items-center w-100"
            >
              <div class="d-flex align-items-center">
                <span
                  v-if="notification.headerBadge === 'unread-mention'"
                  class="support-unread-mention-icon me-2"
                  aria-hidden="true"
                >
                  <i class="fas fa-comment"></i>
                </span>
                <i
                  v-else
                  :class="getIcon(notification.type)"
                  class="me-2"
                ></i>
                <strong>{{ notification.title }}</strong>
              </div>
              <button
                @click="removeNotification(notification.id)"
                class="btn-close"
                type="button"
                :aria-label="$t('Close') || 'Cerrar'"
              ></button>
            </div>
          </div>
          <div class="notification-body">
            <div v-if="notification.html" v-html="notification.html"></div>
            <div v-else>{{ notification.message }}</div>

            <div
              v-if="
                notification.data &&
                Object.keys(notification.data).length > 0
              "
              class="mt-2"
            >
              <small class="text-muted">
                <div
                  v-for="(value, key) in notification.data"
                  :key="key"
                  class="hook-detail"
                >
                  <i class="fas fa-info-circle me-1"></i>
                  <strong>{{ key }}:</strong> {{ value }}
                </div>
              </small>
            </div>

            <div
              v-if="
                notification.type === 'loading' &&
                notification.progress !== undefined
              "
              class="mt-2"
            >
              <div class="progress" style="height: 6px">
                <div
                  class="progress-bar progress-bar-striped progress-bar-animated"
                  :style="{ width: Math.max(Number(notification.progress) || 0, 4) + '%' }"
                ></div>
              </div>
            </div>
          </div>
          <div
            v-if="notification.actions && notification.actions.length > 0"
            class="notification-actions"
          >
            <button
              v-for="action in notification.actions.filter((a) => a.callback)"
              :key="action.text"
              type="button"
              @click="action.callback"
              :class="`btn btn-sm ${action.class || 'btn-primary'}`"
            >
              <i v-if="action.icon" :class="action.icon" class="me-1"></i>
              {{ action.text }}
            </button>
          </div>
          <div v-else-if="notification.action" class="notification-actions">
            <button
              type="button"
              @click="notification.action.callback"
              :class="`btn btn-sm ${
                notification.action.class || 'btn-primary'
              }`"
            >
              <i
                v-if="notification.action.icon"
                :class="notification.action.icon"
                class="me-1"
              ></i>
              {{ notification.action.text }}
            </button>
          </div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import websocketService from "@/services/websocketService";

export default {
  name: "FloatingNotification",
  data() {
    return {
      notifications: [],
      nextId: 1,
    };
  },
  mounted() {
    this.loadNotifications();
    this.setupWebSocket();
  },
  beforeDestroy() {
    websocketService.off("hook_notification", this.handleHookNotification);
  },
  methods: {
    setupWebSocket() {
      const userId = localStorage.getItem("userId") || "anonymous";
      websocketService.connect(userId);
      websocketService.on("hook_notification", this.handleHookNotification);
    },

    handleHookNotification(message) {
      const fullMessage = message.message;
      switch (message.status) {
        case "started":
          this.addNotification({
            type: "info",
            title: `Hook ${message.operation}`,
            message: fullMessage,
            persistent: false,
            data: message.details || {},
          });
          break;
        case "completed":
          this.addNotification({
            type: "success",
            title: `Hook ${message.operation} completado`,
            message: fullMessage,
            persistent: false,
            data: message.details || {},
          });
          break;
        case "error":
          this.addNotification({
            type: "error",
            title: `Error en hook ${message.operation}`,
            message: fullMessage,
            persistent: false,
            data: message.details || {},
          });
          break;
        default:
          break;
      }
    },

    /** No sobreviven a la recarga: quien las crea no puede recuperar su id para quitarlas. */
    isEphemeralNotification(notification) {
      if (this.isEphemeralSystemMigrationNotification(notification)) return true;
      // El aviso de menciones lo vuelve a crear soporte al entrar; guardado se duplicaba
      return !!notification && notification.headerBadge === "unread-mention";
    },

    isEphemeralSystemMigrationNotification(notification) {
      if (!notification || typeof notification !== "object") return false;
      if (notification.kind === "system-migration") return true;
      // Legacy: migration popups saved before `kind` existed
      const mid = notification.data && notification.data.migrationId;
      if (!mid) return false;
      return (
        notification.type === "warning" ||
        notification.type === "loading" ||
        notification.type === "success"
      );
    },

    addNotification(notification) {
      const id = this.nextId++;
      const newNotification = {
        id,
        ...notification,
        timestamp: new Date(),
      };
      this.notifications.push(newNotification);
      this.saveNotifications();

      if (
        (notification.type === "success" || notification.type === "error") &&
        !notification.persistent
      ) {
        setTimeout(() => {
          this.removeNotification(id);
        }, 300000);
      }

      return id;
    },

    saveNotifications() {
      try {
        // System migration UI must not survive reload (component loses notificationId).
        const persistable = this.notifications.filter(
          (n) => !this.isEphemeralNotification(n)
        );
        localStorage.setItem(
          "floatingNotifications",
          JSON.stringify(persistable)
        );
      } catch (error) {
        console.error("Error saving notifications:", error);
      }
    },

    loadNotifications() {
      try {
        const saved = localStorage.getItem("floatingNotifications");
        if (!saved) return;
        const notifications = JSON.parse(saved);
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        this.notifications = notifications.filter((notification) => {
          if (this.isEphemeralNotification(notification)) {
            return false;
          }
          const notificationDate = new Date(notification.timestamp);
          return notificationDate >= oneDayAgo;
        });
        if (this.notifications.length > 0) {
          this.nextId = Math.max(...this.notifications.map((n) => n.id)) + 1;
        }
        this.saveNotifications();
      } catch (error) {
        console.error("Error loading notifications:", error);
      }
    },

    clearAllNotifications() {
      this.notifications = [];
      this.saveNotifications();
    },

    getNotificationsByType(type) {
      return this.notifications.filter((n) => n.type === type);
    },

    updateNotification(id, updates) {
      const notificationIndex = this.notifications.findIndex((n) => n.id === id);
      if (notificationIndex !== -1) {
        const updatedNotification = {
          ...this.notifications[notificationIndex],
          ...updates,
        };
        this.$set(this.notifications, notificationIndex, updatedNotification);
        this.saveNotifications();
      }
    },

    updateProgress(id, progress) {
      this.updateNotification(id, { progress });
    },

    updateMessage(id, message) {
      this.updateNotification(id, { message });
    },

    updateTitle(id, title) {
      this.updateNotification(id, { title });
    },

    convertNotification(id, newType, newTitle, newMessage, options = {}) {
      const notificationIndex = this.notifications.findIndex((n) => n.id === id);
      if (notificationIndex === -1) return;
      const updatedNotification = {
        ...this.notifications[notificationIndex],
        type: newType,
        title: newTitle,
        message: newMessage,
        persistent: options.persistent !== false,
        ...options,
      };
      this.$set(this.notifications, notificationIndex, updatedNotification);
      this.saveNotifications();
      if (newType === "error" && !options.persistent) {
        setTimeout(() => {
          this.removeNotification(id);
        }, 10000);
      }
    },

    removeNotification(id) {
      const index = this.notifications.findIndex((n) => n.id === id);
      if (index > -1) {
        this.notifications.splice(index, 1);
        this.saveNotifications();
      }
    },

    removeNotificationsWhere(predicate) {
      if (typeof predicate !== "function") return [];
      const removedIds = [];
      this.notifications = this.notifications.filter((n) => {
        if (predicate(n)) {
          removedIds.push(n.id);
          return false;
        }
        return true;
      });
      if (removedIds.length > 0) {
        this.saveNotifications();
      }
      return removedIds;
    },

    getIcon(type) {
      const icons = {
        info: "fas fa-info-circle",
        success: "fas fa-check-circle",
        warning: "fas fa-exclamation-triangle",
        error: "fas fa-times-circle",
        loading: "fas fa-spinner fa-spin",
      };
      return icons[type] || icons.info;
    },

    showSuccess(title, message, action = null) {
      return this.addNotification({
        type: "success",
        title,
        message,
        action,
        persistent: false,
      });
    },

    showError(title, message, action = null) {
      return this.addNotification({
        type: "error",
        title,
        message,
        action,
        persistent: false,
      });
    },

    showWarning(title, message, action = null) {
      return this.addNotification({
        type: "warning",
        title,
        message,
        action,
        persistent: false,
      });
    },

    showInfo(title, message, action = null) {
      return this.addNotification({
        type: "info",
        title,
        message,
        action,
        persistent: false,
      });
    },

    showLoading(title, message, persistent = false, data = null) {
      return this.addNotification({
        type: "loading",
        title,
        message,
        persistent,
        data,
      });
    },

    showCustom(type, title, message, options = {}) {
      return this.addNotification({
        type,
        title,
        message,
        ...options,
      });
    },
  },
};
</script>

<style scoped>
.floating-notifications {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  max-width: 400px;
}

.clear-buttons {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.clear-buttons .btn {
  border-radius: 999px;
  font-size: 12px;
}

.notification-item {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-bottom: 10px;
  border-left: 4px solid #ffc107;
  overflow: hidden;
}

.notification-item.success {
  border-left-color: #28a745;
}

.notification-item.warning {
  border-left-color: #ffc107;
}

.notification-item.error {
  border-left-color: #dc3545;
}

.notification-item.loading {
  border-left-color: #17a2b8;
}

.notification-content {
  padding: 12px 16px;
}

.notification-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.notification-body {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.notification-actions {
  display: flex;
  justify-content: flex-end;
}

.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform 0.3s ease;
}

.hook-detail {
  margin-bottom: 2px;
  font-size: 12px;
  line-height: 1.3;
}

.hook-detail strong {
  color: #495057;
  font-weight: 600;
}

/* Badge de mención sin leer (tickets de soporte + popup) */
.support-unread-mention-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 50%;
  background: #dc3545;
  color: #fff;
  font-size: 0.65rem;
  line-height: 1;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.22);
  animation: support-unread-mention-titilar 1.6s ease-in-out infinite;
}
.support-unread-mention-icon .fa-comment {
  margin: 0;
}
@keyframes support-unread-mention-titilar {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.55;
    transform: scale(0.92);
  }
}
</style>
